import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Progress,
  Alert,
} from "antd";
import {
  AreaChartOutlined,
  ShopOutlined,
  TagOutlined,
  AccountBookOutlined,
  InboxOutlined,
  ShoppingOutlined,
  ArrowRightOutlined,
  RiseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import {
  getRetailers,
  Retailer,
} from "../services/retailerApi";
import {
  getAllPurchaseOrders,
  PurchaseOrder,
} from "../services/purchaseOrderService";

const Home: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);

  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // KPIs
  const [activeRetailersCount, setActiveRetailersCount] = useState<number>(0);
  const [pipelineValue, setPipelineValue] = useState<number>(0);
  const [totalSellInYTD, setTotalSellInYTD] = useState<number>(0);
  const [pendingShipments, setPendingShipments] = useState<number>(0);

  useEffect(() => {
    const fetchB2BData = async () => {
      try {
        setLoading(true);
        const [retailerData, orderData] = await Promise.all([
          getRetailers(),
          getAllPurchaseOrders()
        ]);

        setRetailers(retailerData);
        setOrders(orderData);

        // Calculate Active Network
        const active = retailerData.filter(r => r.status === 'active').length;
        setActiveRetailersCount(active);

        // Calculate Pipeline Value (Discovery to Onboarding)
        const crmValue = retailerData
          .filter(r => r.status === 'prospect' && r.crm)
          .reduce((sum, r) => sum + (r.crm?.estimatedValue || 0), 0);
        setPipelineValue(crmValue);

        // Calculate Sell-in YTD (from outbound orders)
        const currentYear = new Date().getFullYear();
        const ytdSales = orderData
          .filter(o => 
            o.type === 'outbound' && 
            o.status !== 'cancelled' && 
            o.creationDate && new Date(o.creationDate).getFullYear() === currentYear
          )
          .reduce((sum, o) => sum + (o.totalAmount || 0), 0);
        setTotalSellInYTD(ytdSales);

        // Calculate Pending Shipments
        const pending = orderData.filter(o => 
          o.type === 'outbound' && 
          ['sent', 'in_delivery', 'partially_received'].includes(o.status || '')
        ).length;
        setPendingShipments(pending);

      } catch (e) {
        const msg = e instanceof Error ? e.message : "Erreur inconnue";
        setError(`Erreur lors du chargement des données B2B: ${msg}`);
      } finally {
        setLoading(false);
      }
    };
    fetchB2BData();
  }, []);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value || 0);

  return (
    <div className="home-cockpit">
      {/* Neo Welcome Section */}
      <div className="welcome-container">
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={16}>
            <div className="welcome-text">
              <Typography.Text className="welcome-label">CONSOLE B2B</Typography.Text>
              <Typography.Title level={1} className="welcome-title">
                Bonjour, {user.currentUser?.firstName || "Utilisateur"}
              </Typography.Title>
              <Typography.Paragraph className="welcome-description">
                Pilotage global de l'activité B2B. Suivez le déploiement de votre réseau et les flux logistiques en temps réel.
              </Typography.Paragraph>
              <Space size={12}>
                <Link to="/distributeurs">
                  <Button type="primary" className="neo-btn-primary" icon={<ShopOutlined />}>
                    Réseau Retail
                  </Button>
                </Link>
                <Link to="/approvisionnement">
                  <Button className="neo-btn-secondary" icon={<PlusOutlined />}>
                    Nouvelle Expédition
                  </Button>
                </Link>
              </Space>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="global-health-card">
              <Card className="neo-card-dark">
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography.Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600 }}>SANTÉ DU RÉSEAU</Typography.Text>
                    <AreaChartOutlined style={{ color: 'white' }} />
                  </div>
                  
                  <div className="health-stats">
                    <div className="stat-item">
                      <div className="stat-value" style={{ color: 'white', fontSize: 24, fontWeight: 800 }}>{activeRetailersCount}</div>
                      <div className="stat-label" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>Distributeurs Actifs</div>
                    </div>
                    <Progress
                      percent={Math.round((activeRetailersCount / (retailers.length || 1)) * 100)}
                      strokeColor="#10b981"
                      trailColor="rgba(255,255,255,0.1)"
                      showInfo={false}
                    />
                  </div>

                  <div className="health-badge">
                    <Progress
                      type="circle"
                      percent={82}
                      size={40}
                      strokeColor="#3b82f6"
                      format={(p) => <span style={{ color: 'white', fontSize: 10 }}>{p}%</span>}
                    />
                    <div style={{ marginLeft: 12 }}>
                      <div style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>Taux de Rétention</div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>Réseau stable</div>
                    </div>
                  </div>
                </Space>
              </Card>
            </div>
          </Col>
        </Row>
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 24, borderRadius: 12 }} />}

      {/* KPI Row */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="neo-card kpi-card" loading={loading}>
            <div className="kpi-header">
              <Typography.Text className="kpi-label">SELL-IN YTD</Typography.Text>
              <div className="kpi-icon blue"><RiseOutlined /></div>
            </div>
            <div className="kpi-value">{formatCurrency(totalSellInYTD)}</div>
            <div className="kpi-trend up">
              <RiseOutlined /> +12.4% <span className="trend-text">vs N-1</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="neo-card kpi-card" loading={loading}>
            <div className="kpi-header">
              <Typography.Text className="kpi-label">PIPELINE CRM</Typography.Text>
              <div className="kpi-icon orange"><ShoppingOutlined /></div>
            </div>
            <div className="kpi-value">{formatCurrency(pipelineValue)}</div>
            <div className="kpi-trend up">
              <Typography.Text strong style={{ color: '#f59e0b' }}>{retailers.filter(r => r.status === 'prospect').length}</Typography.Text>
              <span className="trend-text"> opportunités</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="neo-card kpi-card" loading={loading}>
            <div className="kpi-header">
              <Typography.Text className="kpi-label">EXPÉDITIONS EN COURS</Typography.Text>
              <div className="kpi-icon green"><InboxOutlined /></div>
            </div>
            <div className="kpi-value">{pendingShipments}</div>
            <div className="kpi-trend up">
              <Progress percent={94} size="small" strokeColor="#10b981" />
              <span className="trend-text" style={{ marginLeft: 8 }}>OTIF</span>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="neo-card kpi-card" loading={loading}>
            <div className="kpi-header">
              <Typography.Text className="kpi-label">MARGE RÉSEAU</Typography.Text>
              <div className="kpi-icon purple"><AreaChartOutlined /></div>
            </div>
            <div className="kpi-value">42.8%</div>
            <div className="kpi-trend up">
              <RiseOutlined /> +0.5% <span className="trend-text">optimisée</span>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Modules Access */}
        <Col xs={24} lg={16}>
          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={4} style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Navigation Rapide</Typography.Title>
          </div>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12}>
              <Card hoverable className="neo-card module-access-card" onClick={() => window.location.href = '/distributeurs'}>
                <div className="module-icon-bg blue"><ShopOutlined /></div>
                <Typography.Title level={4}>Retailers & CRM</Typography.Title>
                <Typography.Paragraph type="secondary">Gestion du réseau actif et pipeline d'onboarding.</Typography.Paragraph>
                <div className="card-footer">
                  <span className="action-text">Ouvrir le cockpit</span>
                  <ArrowRightOutlined />
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card hoverable className="neo-card module-access-card" onClick={() => window.location.href = '/approvisionnement'}>
                <div className="module-icon-bg orange"><InboxOutlined /></div>
                <Typography.Title level={4}>Logistique B2B</Typography.Title>
                <Typography.Paragraph type="secondary">Pilotage des flux entrants et expéditions.</Typography.Paragraph>
                <div className="card-footer">
                  <span className="action-text">Voir les flux</span>
                  <ArrowRightOutlined />
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card hoverable className="neo-card module-access-card" onClick={() => window.location.href = '/finance'}>
                <div className="module-icon-bg purple"><AccountBookOutlined /></div>
                <Typography.Title level={4}>Finance & Cashflow</Typography.Title>
                <Typography.Paragraph type="secondary">Suivi de la trésorerie et KPIs financiers consolidés.</Typography.Paragraph>
                <div className="card-footer">
                  <span className="action-text">Voir les comptes</span>
                  <ArrowRightOutlined />
                </div>
              </Card>
            </Col>
            <Col xs={24} sm={12}>
              <Card hoverable className="neo-card module-access-card" onClick={() => window.location.href = '/catalogue'}>
                <div className="module-icon-bg emerald"><TagOutlined /></div>
                <Typography.Title level={4}>Catalogue & Tarifs</Typography.Title>
                <Typography.Paragraph type="secondary">Gestion de l'offre produit et des conditions B2B.</Typography.Paragraph>
                <div className="card-footer">
                  <span className="action-text">Gérer l'offre</span>
                  <ArrowRightOutlined />
                </div>
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Sidebar Insights */}
        <Col xs={24} lg={8}>
          <div style={{ marginBottom: 16 }}>
            <Typography.Title level={4} style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Insights & Actions</Typography.Title>
          </div>
          <Card className="neo-card action-sidebar">
            <Typography.Text strong className="sidebar-section-title">CRM & ONBOARDING</Typography.Text>
            <div className="insight-list" style={{ marginBottom: 32 }}>
              {retailers.filter(r => r.crm?.onboardingStage === 'negotiation' || r.crm?.onboardingStage === 'contract').slice(0, 3).map(r => (
                <div key={r.id || r._id} className="insight-row">
                  <div className="insight-indicator blue"></div>
                  <Typography.Text className="insight-text">
                    <Typography.Text strong>{r.name}</Typography.Text> en {r.crm?.onboardingStage === 'contract' ? 'signature' : 'négociation'}
                  </Typography.Text>
                </div>
              ))}
              {retailers.filter(r => r.crm?.onboardingStage === 'negotiation' || r.crm?.onboardingStage === 'contract').length === 0 && (
                <Typography.Text type="secondary" italic>Aucune négociation chaude</Typography.Text>
              )}
            </div>

            <Typography.Text strong className="sidebar-section-title">LOGISTIQUE CRITIQUE</Typography.Text>
            <div className="insight-list">
              <div className="insight-row">
                <div className="insight-indicator red"></div>
                <Typography.Text className="insight-text">
                  <Typography.Text strong>{orders.filter(o => o.status === 'dispute').length}</Typography.Text> litiges transport en cours
                </Typography.Text>
              </div>
              <div className="insight-row">
                <div className="insight-indicator orange"></div>
                <Typography.Text className="insight-text">
                  <Typography.Text strong>{orders.filter(o => o.status === 'partially_received').length}</Typography.Text> réceptions partielles
                </Typography.Text>
              </div>
              <div className="insight-row">
                <div className="insight-indicator green"></div>
                <Typography.Text className="insight-text">
                  Prévisions sell-out <Typography.Text strong>+8%</Typography.Text>
                </Typography.Text>
              </div>
            </div>
            
            <Button block className="sidebar-action-btn" icon={<PlusOutlined />} style={{ marginTop: 32 }} onClick={() => window.location.href = '/approvisionnement'}>
              Nouvelle Expédition
            </Button>
          </Card>
        </Col>
      </Row>

      <style>{`
        .home-cockpit {
          padding: 32px;
          min-height: 100vh;
          background: #f8fafc;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        
        .welcome-container {
          background: #ffffff;
          padding: 40px;
          border-radius: 24px;
          border: 1px solid #e2e8f0;
          margin-bottom: 40px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
        }
        .welcome-label {
          display: block;
          color: #64748b;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.1em;
          margin-bottom: 12px;
        }
        .welcome-title {
          margin: 0 0 16px 0 !important;
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #0f172a;
          font-size: 42px !important;
        }
        .welcome-description {
          font-size: 16px;
          color: #64748b;
          max-width: 600px;
          margin-bottom: 32px !important;
          line-height: 1.6;
        }

        .neo-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .neo-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
        }

        .neo-card-dark {
          background: #0f172a;
          border-radius: 20px;
          padding: 24px;
          border: none;
        }
        
        .health-badge {
          display: flex;
          align-items: center;
          padding: 16px;
          background: rgba(255,255,255,0.05);
          border-radius: 12px;
          margin-top: 8px;
        }

        /* KPI Styling */
        .kpi-card {
          padding: 24px;
        }
        .kpi-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }
        .kpi-label {
          color: #64748b;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.05em;
        }
        .kpi-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        }
        .kpi-icon.blue { background: #eff6ff; color: #3b82f6; }
        .kpi-icon.orange { background: #fff7ed; color: #f59e0b; }
        .kpi-icon.green { background: #ecfdf5; color: #10b981; }
        .kpi-icon.red { background: #fef2f2; color: #ef4444; }
        
        .kpi-value {
          font-size: 28px;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 8px;
          letter-spacing: -0.02em;
        }
        .kpi-trend {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          font-weight: 600;
        }
        .kpi-trend.up { color: #10b981; }
        .kpi-trend.down { color: #ef4444; }
        .trend-text { color: #94a3b8; font-weight: 400; margin-left: 4px; }

        /* Module Access Cards */
        .module-access-card {
          padding: 32px 24px;
          cursor: pointer;
        }
        .module-access-card h4 {
          margin: 20px 0 8px 0 !important;
          font-weight: 700;
        }
        .module-icon-bg {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }
        .module-icon-bg.purple { background: #f5f3ff; color: #8b5cf6; }
        .module-icon-bg.blue { background: #eff6ff; color: #3b82f6; }
        .module-icon-bg.emerald { background: #ecfdf5; color: #10b981; }
        .module-icon-bg.gray { background: #f1f5f9; color: #94a3b8; }
        
        .card-footer {
          margin-top: 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #0f172a;
          font-weight: 600;
          font-size: 14px;
        }
        .module-access-card.disabled {
          cursor: default;
          opacity: 0.7;
        }

        /* Sidebar Action Styles */
        .action-sidebar {
          padding: 24px;
        }
        .sidebar-section-title {
          display: block;
          font-size: 11px;
          color: #94a3b8;
          letter-spacing: 0.1em;
          margin-bottom: 16px;
        }
        .sidebar-action-btn {
          height: 44px;
          border-radius: 10px;
          font-weight: 600;
          border: 1px solid #e2e8f0;
          text-align: left;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          transition: all 0.2s;
        }
        .sidebar-action-btn:hover {
          border-color: #0f172a;
          color: #0f172a;
          background: #f8fafc;
        }
        
        .insight-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .insight-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .insight-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }
        .insight-indicator.red { background: #ef4444; }
        .insight-indicator.orange { background: #f59e0b; }
        .insight-indicator.green { background: #10b981; }
        .insight-text { font-size: 14px; color: #475569; }

        /* Buttons & Selects */
        .neo-btn-primary {
          background: #0f172a !important;
          border: none !important;
          height: 48px !important;
          padding: 0 32px !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
        }
        .neo-btn-secondary {
          height: 48px !important;
          padding: 0 32px !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
          border: 1px solid #e2e8f0 !important;
        }
        
        .neo-select-dark .ant-select-selector {
          background: rgba(255,255,255,0.1) !important;
          border-radius: 10px !important;
          color: white !important;
          height: 40px !important;
          display: flex !important;
          align-items: center !important;
        }
        .neo-select-dark .ant-select-selection-item { color: white !important; font-weight: 600; }
        .neo-select-dark .ant-select-arrow { color: rgba(255,255,255,0.5); }
      `}</style>
    </div>
  );
};

export default Home;
